import { openRouter } from "./openRouterClient";

const jobDescriptionSystemPrompt = `
You are a professional job description writer. Your task is to create compelling, professional job descriptions based on the provided job details.

Guidelines:
- Write in a professional, engaging tone
- Include company overview, role responsibilities, and requirements
- Use bullet points for responsibilities and requirements
- Keep descriptions between 200-400 words
- Make it attractive to potential candidates
- Use proper HTML formatting for structure

Return only the HTML content without any code block markers, explanations, or extra formatting.
`;

const rewriteSystemPrompt = `
You are a professional job description editor. Your task is to rewrite and improve existing job descriptions while maintaining the core information.

Guidelines:
- Improve clarity, structure, and engagement
- Fix grammar and formatting issues
- Make the content more compelling and professional
- Maintain all key information from the original
- Use proper HTML formatting
- Keep similar length to original

Return only the HTML content without any code block markers, explanations, or extra formatting.
`;

const stripHtmlTags = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
};

const cleanAIResponse = (response) => {
  if (!response) return "";

  let cleaned = response.trim();

  // Remove markdown code blocks (```html or ``` at start/end)
  cleaned = cleaned.replace(/^```html?\n?/i, "");
  cleaned = cleaned.replace(/^```\n?/i, "");
  cleaned = cleaned.replace(/\n?```$/i, "");

  // Remove any leading/trailing explanatory text that might contain "html" or similar
  const htmlStart = cleaned.search(/<[^>]*>/);
  const lastHtmlEnd = cleaned.lastIndexOf(">") + 1;

  if (htmlStart !== -1 && lastHtmlEnd > htmlStart) {
    cleaned = cleaned.substring(htmlStart, lastHtmlEnd);
  }

  // Clean up any extra whitespace
  cleaned = cleaned.trim();

  return cleaned;
};

export const aiJobDescriptionService = {
  generateJobDescription: async (jobData) => {
    try {
      console.log(
        "🤖 Starting AI job description generation with data:",
        jobData,
      );

      const {
        company_name,
        location,
        role_title,
        reports_to,
        job_level,
        job_type,
        industry,
        country,
      } = jobData;

      if (!role_title && !company_name) {
        throw new Error(
          "Please provide at least a role title and company name to generate a job description",
        );
      }

      if (!role_title) {
        throw new Error("Role title is required to generate a job description");
      }

      if (!company_name) {
        throw new Error(
          "Company name is required to generate a job description",
        );
      }

      let levelText = "Not specified";
      if (job_level) {
        if (typeof job_level === "object" && job_level.title) {
          levelText = job_level.title;
        } else if (typeof job_level === "string" && job_level !== "0") {
          levelText = job_level;
        } else if (typeof job_level === "number" && job_level !== 0) {
          levelText = `Level ${job_level}`;
        }
      }

      let industryText = "Not specified";
      if (industry) {
        if (typeof industry === "object" && industry.title) {
          industryText = industry.title;
        } else if (typeof industry === "string" && industry !== "0") {
          industryText = industry;
        }
      }

      let jobTypeText = "Not specified";
      if (job_type) {
        if (typeof job_type === "object" && job_type.title) {
          jobTypeText = job_type.title;
        } else if (typeof job_type === "string" && job_type !== "0") {
          jobTypeText = job_type;
        }
      }

      const prompt = `
Create a professional job description for the following position:

Company: ${company_name}
Role: ${role_title}
Industry: ${industryText}
Location: ${location || "Remote/Flexible"}
Job Type: ${jobTypeText}
Job Level: ${levelText}
Reports To: ${reports_to || "Not specified"}

IMPORTANT: Do NOT include the job title or "Job Description" as a heading at the very beginning of your response. Start directly with the Company Overview or a brief introduction.

Please create a comprehensive job description that includes:
1. Brief company overview (2-3 sentences about the company)
2. Role summary (what the position is about)
3. Key responsibilities (4-6 bullet points starting with action verbs)
4. Required qualifications (3-5 bullet points including skills and experience)
5. Nice to have qualifications (2-3 bullet points - optional)
6. What we offer section (benefits, growth opportunities, etc.)

Format with proper HTML tags for structure (use <h5> for section headings, <p>, <ul>, <li>, <strong>, etc.).
Make it professional, engaging, and attractive to potential candidates.
Keep the total length between 250-400 words.
      `;

      console.log("📤 Sending prompt to OpenRouter API...");

      const response = await openRouter.chat([
        { role: "system", content: jobDescriptionSystemPrompt },
        { role: "user", content: prompt },
      ]);

      console.log(
        "📥 Received response from OpenRouter:",
        response?.substring(0, 200) + "...",
      );

      if (!response || response.trim() === "") {
        throw new Error("Received empty response from AI service");
      }

      if (response.length < 50) {
        throw new Error("Generated content seems too short. Please try again.");
      }

      // Clean the AI response to remove any markdown formatting or extra text
      const cleanedResponse = cleanAIResponse(response);

      return cleanedResponse;
    } catch (error) {
      console.error("❌ AI Job Description Generation Error:", error);

      if (error.message.includes("API key")) {
        throw new Error(
          "AI service configuration error. Please check your OpenRouter API key in environment variables.",
        );
      } else if (
        error.message.includes("network") ||
        error.message.includes("NETWORK_ERROR")
      ) {
        throw new Error(
          "Network connection error. Please check your internet connection and try again.",
        );
      } else if (
        error.message.includes("rate limit") ||
        error.message.includes("429")
      ) {
        throw new Error(
          "AI service rate limit reached. Please wait a moment and try again.",
        );
      } else if (
        error.message.includes("insufficient credits") ||
        error.message.includes("402")
      ) {
        throw new Error(
          "Insufficient AI service credits. Please check your OpenRouter account.",
        );
      } else if (error.message.includes("OpenRouter API failed")) {
        throw new Error(`AI service error: ${error.message}`);
      } else if (
        error.message.includes("Please provide") ||
        error.message.includes("required")
      ) {
        throw error;
      } else {
        throw new Error(`Failed to generate job description: ${error.message}`);
      }
    }
  },

  rewriteJobDescription: async (existingDescription, jobData = {}) => {
    try {
      console.log("🔄 Starting AI job description rewrite...");
      console.log(
        "📝 Existing content length:",
        existingDescription?.length || 0,
      );
      console.log("📋 Job data:", jobData);

      // Validate existing description
      const cleanedDescription = stripHtmlTags(existingDescription);
      if (!cleanedDescription || cleanedDescription.trim() === "") {
        throw new Error(
          "No existing content found to rewrite. Please add some content first.",
        );
      }

      if (cleanedDescription.length < 20) {
        throw new Error(
          "Content is too short to rewrite effectively. Please add more content first.",
        );
      }

      const contextInfo = jobData.role_title
        ? `\n\nContext: This is for a ${jobData.role_title} position${
            jobData.company_name ? ` at ${jobData.company_name}` : ""
          }.`
        : "";

      const prompt = `
Please rewrite and improve the following job description to make it more professional, engaging, and well-structured:

ORIGINAL CONTENT:
${existingDescription}
${contextInfo}

Instructions:
- Keep all the essential information from the original
- Improve the structure, flow, and readability
- Make the language more professional and engaging
- Fix any grammar or formatting issues
- Use proper HTML formatting with appropriate tags (use <h5> for section headings)
- Maintain similar length to the original (don't make it much longer or shorter)
- Make it more attractive to potential job candidates
- Ensure bullet points are well-organized and action-oriented

Return only the improved HTML-formatted job description, no explanations or comments.
      `;

      console.log("📤 Sending rewrite request to OpenRouter API...");

      const response = await openRouter.chat([
        { role: "system", content: rewriteSystemPrompt },
        { role: "user", content: prompt },
      ]);

      console.log(
        "📥 Received rewrite response from OpenRouter:",
        response?.substring(0, 200) + "...",
      );

      if (!response || response.trim() === "") {
        throw new Error("Received empty response from AI service");
      }

      if (response.length < 50) {
        throw new Error("Rewritten content seems too short. Please try again.");
      }

      // Clean the AI response to remove any markdown formatting or extra text
      const cleanedResponse = cleanAIResponse(response);

      return cleanedResponse;
    } catch (error) {
      console.error("❌ AI Job Description Rewrite Error:", error);

      if (error.message.includes("API key")) {
        throw new Error(
          "AI service configuration error. Please check your OpenRouter API key.",
        );
      } else if (
        error.message.includes("No existing content") ||
        error.message.includes("too short")
      ) {
        throw error;
      } else if (
        error.message.includes("rate limit") ||
        error.message.includes("429")
      ) {
        throw new Error(
          "AI service rate limit reached. Please wait a moment and try again.",
        );
      } else if (
        error.message.includes("insufficient credits") ||
        error.message.includes("402")
      ) {
        throw new Error(
          "Insufficient AI service credits. Please check your OpenRouter account.",
        );
      } else {
        throw new Error(`Failed to rewrite job description: ${error.message}`);
      }
    }
  },
};
